/* Isolated presentation controller: no product forms, app hooks or pricing. */
(() => {
  if (window.hgProductVideoReady) return;
  window.hgProductVideoReady = true;
  const touch = matchMedia('(hover: none), (pointer: coarse)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const states = new Map();
  let timer, frame;
  const autoAllowed = () => !document.hidden && !reduced.matches && !navigator.connection?.saveData;
  const visible = v => {
    const r = v.getBoundingClientRect();
    const h = Math.max(0, Math.min(r.bottom, innerHeight) - Math.max(r.top, 0));
    const w = Math.max(0, Math.min(r.right, innerWidth) - Math.max(r.left, 0));
    return { ratio: h * w / Math.max(1, Math.min(r.height, innerHeight) * Math.min(r.width, innerWidth)), distance: Math.abs((r.top + r.bottom) / 2 - innerHeight / 2) };
  };
  function label(s) {
    if (!s.button) return;
    const text = s.v.paused ? 'Play video' : 'Pause video';
    s.button.textContent = text;
    s.button.setAttribute('aria-label', text.replace('video', 'product video'));
  }
  function stop(s, release = false) {
    s.wanted = false;
    s.v.pause();
    s.v.classList.remove('is-playing');
    if (release && s.v.hasAttribute('src')) {
      s.v.removeAttribute('src');
      s.v.load();
    }
    label(s);
  }
  function play(s, manual = false) {
    if (s.wanted || (s.failed && !manual)) return;
    s.failed = false;
    const v = s.v;
    if (!v.hasAttribute('src')) {
      const sources = Array.from(v.querySelectorAll('source[data-src]'));
      const target = Math.min(s.card ? 720 : 1920, v.clientWidth * Math.min(devicePixelRatio || 1, 2));
      const source = sources.find(n => Number(n.dataset.width) >= target) || sources.at(-1);
      if (!source) return;
      v.src = source.dataset.src;
      v.load();
    }
    s.wanted = true;
    v.muted = true;
    v.defaultMuted = true;
    const attempt = v.play();
    if (attempt) attempt.catch(error => {
      // A cancelled pending play is expected when the visitor scrolls away.
      if (s.wanted && error.name !== 'AbortError') { s.failed = true; stop(s); }
    });
  }
  function choose() {
    const candidates = [];
    states.forEach(s => {
      if (!s.v.isConnected) { stop(s, true); observer?.unobserve(s.v); states.delete(s.v); return; }
      s.view = visible(s.v);
      if (s.card && s.view.ratio >= .6) candidates.push(s);
      if (s.view.ratio < (s.card ? .6 : .45) || document.hidden) stop(s, s.view.ratio === 0);
    });
    candidates.sort((a, b) => a.view.distance - b.view.distance || b.view.ratio - a.view.ratio);
    return new Set(candidates.slice(0, 2));
  }
  function reconcile(start = false) {
    const selected = choose();
    states.forEach(s => {
      const eligible = s.card
        ? autoAllowed() && (touch.matches ? selected.has(s) : s.hovered && s.view.ratio >= .6)
        : !document.hidden && s.view.ratio >= .45 && !s.userPaused && (autoAllowed() || s.manual);
      if (!eligible) stop(s);
      else if (start) play(s);
    });
  }
  function schedule() {
    clearTimeout(timer);
    if (!frame) frame = requestAnimationFrame(() => { frame = null; reconcile(false); });
    timer = setTimeout(() => reconcile(true), 1200);
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(schedule, {threshold:[0,.45,.6,1]}) : null;
  function scan() {
    document.querySelectorAll('[data-hg-video]').forEach(v => {
      if (states.has(v)) return;
      const s = {v, card:v.dataset.hgVideo === 'card', wanted:false, hovered:false};
      states.set(v, s);
      v.addEventListener('playing', () => {
        if (!s.wanted || document.hidden) { stop(s); return; }
        v.classList.add('is-playing'); label(s);
      });
      v.addEventListener('pause', () => label(s));
      v.addEventListener('error', () => { s.failed = true; stop(s); });
      if (s.card) {
        const card = v.closest('.product-card');
        card.addEventListener('pointerenter', () => {
          s.hovered = true;
          if (!touch.matches) reconcile(true);
        });
        card.addEventListener('pointerleave', () => { s.hovered = false; if (!touch.matches) stop(s, true); });
      } else {
        s.button = v.closest('[data-hg-film]')?.querySelector('[data-hg-film-toggle]');
        s.button?.addEventListener('click', () => {
          if (s.wanted || !v.paused) { s.userPaused = true; s.manual = false; stop(s); }
          else { s.userPaused = false; s.manual = true; play(s, true); }
        });
      }
      observer?.observe(v);
    });
    schedule();
  }
  addEventListener('scroll', schedule, {passive:true});
  addEventListener('resize', schedule, {passive:true});
  document.addEventListener('visibilitychange', () => { reconcile(false); schedule(); });
  addEventListener('pagehide', () => states.forEach(s => stop(s, true)));
  addEventListener('pageshow', schedule);
  touch.addEventListener('change', schedule);
  reduced.addEventListener('change', () => { reconcile(false); schedule(); });
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', schedule);
  let scanQueued = false;
  new MutationObserver(records => {
    if (scanQueued || !records.some(r => [...r.addedNodes, ...r.removedNodes].some(n => n.nodeType === 1 && (n.matches?.('[data-hg-video]') || n.querySelector?.('[data-hg-video]'))))) return;
    scanQueued = true;
    queueMicrotask(() => { scanQueued = false; scan(); });
  }).observe(document.body, {childList:true, subtree:true});
  scan();
})();
